import Card, { type CardNames } from '@/components/Card/Card.tsx';
import { MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE, MINIMUM_SPEND_PAYMENT_CAPTION } from '@/lib/labels.ts';
import styles from '../EventPage/EventPage.module.css';

/**
 * The amount charged to the card now. Renders under the wallet deduction rows when
 * credits or Fun Coupons apply, and on its own for a charge that differs from the
 * bold Minimum spend with no wallet (e.g. prepaid tax or service charges).
 * @param props - Formatted charge, optional card, and whether to show the Amount Due label
 * @returns Charge row with an optional card icon
 * @example <RestaurantAmountDueRow amountDueCurrency="$175.00" cardName="visa" cardLast4="4444" />
 * @example <RestaurantAmountDueRow amountDueCurrency="$175.00" cardName="visa" cardLast4="4444" showLabel={false} />
 */
export function RestaurantAmountDueRow({
  amountDueCurrency,
  cardName,
  cardLast4,
  showLabel = true,
}: {
  /** Leftover charged to the card today */
  amountDueCurrency: string;
  /** Brand icon next to the leftover */
  cardName?: CardNames | null;
  /** Last four of the card that will be charged */
  cardLast4?: string | null;
  /** Confirmation hides Amount Due and puts the card on the left */
  showLabel?: boolean;
}) {
  const cardIdentity = (
    <div className={styles.orderSummaryAmountDueValue}>
      {cardName ? (
        <span className={styles.orderSummaryCardIcon}>
          <Card name={cardName} />
        </span>
      ) : null}
      {cardLast4 ? <p className={styles.orderSummaryPriceLabel}>{cardLast4}</p> : null}
    </div>
  );

  return (
    <div className={styles.orderSummaryPriceRow}>
      {showLabel ? <p className={styles.orderSummaryPriceLabel}>Amount Due</p> : cardIdentity}
      {showLabel ? (
        <div className={styles.orderSummaryAmountDueValue}>
          {cardName ? (
            <span className={styles.orderSummaryCardIcon}>
              <Card name={cardName} />
            </span>
          ) : null}
          {cardLast4 ? <p className={styles.orderSummaryPriceValue}>{cardLast4}</p> : null}
          <p className={styles.orderSummaryPriceValue}>{amountDueCurrency}</p>
        </div>
      ) : (
        <p className={styles.orderSummaryPriceValue}>{amountDueCurrency}</p>
      )}
    </div>
  );
}

/**
 * Per-person line, full table minimum, caption, and the card used for overages.
 * Wallet deductions and Amount Due stay in the parent so they sit together.
 * @param props - Table minimum copy, caption visibility, and optional card on file
 * @returns The per-person line, Minimum spend, caption, and payment-method note
 * @example <RestaurantMinimumSpendTotals minimumSpendCurrency="$525.00" showCaption paymentMethodLast4="4242" />
 */
export function RestaurantMinimumSpendTotals({
  pricePerPersonCurrency,
  minimumSpendCurrency,
  showCaption,
  paymentMethodLast4,
  paymentMethodCardName,
}: {
  /** Formatted per-person minimum, when the preview has one */
  pricePerPersonCurrency?: string | null;
  /** Full table minimum for the party */
  minimumSpendCurrency: string;
  /** True shows the food-and-drink caption */
  showCaption: boolean;
  /** Last four of the card on file, when this booking needs a payment method */
  paymentMethodLast4?: string | null;
  /** Brand icon for the card on file */
  paymentMethodCardName?: CardNames | null;
}) {
  return (
    <div>
      {pricePerPersonCurrency ? (
        <p className={styles.orderSummaryPerPerson}>{pricePerPersonCurrency} minimum per person</p>
      ) : null}
      <div className={styles.orderSummaryTotal}>
        <p className={styles.orderSummaryTotalLabel}>Minimum spend</p>
        <p className={styles.orderSummaryTotalValue}>{minimumSpendCurrency}</p>
      </div>
      {showCaption ? (
        <p className={styles.orderSummaryMinimumSpendNote}>{MINIMUM_SPEND_PAYMENT_CAPTION}</p>
      ) : null}
      {paymentMethodLast4 ? (
        <>
          <div className={styles.orderSummaryMinimumSpendDivider} />
          <div className={styles.orderSummaryPaymentMethod}>
            <p className={styles.orderSummaryPaymentMethodLine}>
              <span>Payment method:</span>
              {paymentMethodCardName ? (
                <span className={styles.orderSummaryCardIcon}>
                  <Card name={paymentMethodCardName} />
                </span>
              ) : null}
              <span>{paymentMethodLast4}</span>
            </p>
            <p className={styles.orderSummaryOverageNote}>{MINIMUM_SPEND_OVERAGE_PAYMENT_NOTE}</p>
          </div>
        </>
      ) : null}
    </div>
  );
}
